<template>
  <div class="phone-input--wrap" :class="{'phone-input--wrap--readonly': !allowEdit}">
    <div class="phone-input" :class="{ 'has-error': error }">
      <div class="country-emoji--wrap" @click="toggleCountryDropdown">
        <h5 v-if="activeCountry && activeCountry.id">
          <span :class="`fi fi-${activeCountry.id.toLowerCase()}`"></span>
        </h5>
        <fluent-icon v-else icon="globe" class="fluent-icon" size="16" />
        <fluent-icon v-if="allowEdit" icon="chevron-down" class="fluent-icon" size="12" />
      </div>
      <span v-if="activeDialCode" class="country-dial--code">
        {{ activeDialCode }}
      </span>
      <input
        :value="phoneNumber"
        type="tel"
        class="phone-input--field"
        :placeholder="placeholder || '(99) 99999-9999'"
        :readonly="!allowEdit"
        :style="styles"
        maxlength="15"
        @input="onMaskedInput"
        @blur="onBlur"
        @paste="onPaste"        
      />
    </div>
    <div v-if="showDropdown && allowEdit" ref="dropdown" class="country-dropdown">
      <div class="dropdown-search--wrap">
        <input
          ref="searchbar"
          v-model="searchCountry"
          type="text"
          placeholder="Search"
          class="dropdown-search"
        />
      </div>
      <div
        v-for="(country, index) in filteredCountriesBySearch"
        ref="dropdownItem"
        :key="index"
        class="country-dropdown--item"
        :class="{
          active: country.id === activeCountryCode,
          focus: index === selectedIndex,
        }"
        @click="onSelectCountry(country)"
      >
        <span :class="`fi fi-${country.id.toLowerCase()}`"></span>

        <span class="country-name">
          {{ country.name }}
        </span>
        <span class="country-dial-code">{{ country.dial_code }}</span>
      </div>
      <div v-if="filteredCountriesBySearch.length === 0">
        <span class="no-results">No results found</span>
      </div>
    </div>
  </div>
</template>

<script>
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';

import {
  hasPressedArrowUpKey,
  hasPressedArrowDownKey,
  isEnter,
} from 'shared/helpers/KeyboardHelpers';

export default {
  mixins: [eventListenerMixins],
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Boolean,
      default: false,
    },
    encrypt: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countries: [
        {
          name: 'Select Country',
          dial_code: '',
          emoji: '',
          id: '',
        },
        ...countries,
      ],
      selectedIndex: -1,
      showDropdown: false,
      searchCountry: '',
      activeCountryCode: 'BR',
      activeDialCode: '+55',
      phoneNumber: this.value
    };
  },
  computed: {
    filteredCountriesBySearch() {
      return this.countries.filter(country => {
        const { name, dial_code, id } = country;
        const search = this.searchCountry.toLowerCase();
        return (
          name.toLowerCase().includes(search) ||
          dial_code.toLowerCase().includes(search) ||
          id.toLowerCase().includes(search)
        );
      });
    },
    activeCountry() {
      if (this.activeCountryCode) {
        return this.countries.find(
          country => country.id === this.activeCountryCode
        );
      }
      return '';
    },
    allowEdit() {
      return !this.readonly && !this.encrypt;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.phoneNumber = this.maskPhoneNumber(newVal);        
      },
      immediate: true
    },
    encrypt: {
      handler() {
        this.phoneNumber = this.maskPhoneNumber(this.value);        
      },
      immediate: true
    }
  },  
  mounted() {
    window.addEventListener('mouseup', this.onOutsideClick);
    if (!this.value) {
      this.activeCountryCode = 'BR';
      this.activeDialCode = '+55';
    } else {
      this.setActiveCountry();
    }
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onOutsideClick);
  },
  methods: {
    onOutsideClick(e) {
      if (
        this.showDropdown &&
        e.target !== this.$refs.dropdown &&
        !this.$refs.dropdown.contains(e.target)
      ) {
        this.closeDropdown();
      }
    },
    onChange(e) {
      this.phoneNumber = e.target.value;
      this.$emit('input', e.target.value, this.activeDialCode);
    },
    onBlur(e) {
      this.$emit('blur', e.target.value);
    },
    dropdownItem() {
      return Array.from(
        this.$refs.dropdown.querySelectorAll(
          'div.country-dropdown div.country-dropdown--item'
        )
      );
    },
    focusedItem() {
      return Array.from(
        this.$refs.dropdown.querySelectorAll('div.country-dropdown div.focus')
      );
    },
    focusedItemIndex() {
      return Array.from(this.dropdownItem()).indexOf(this.focusedItem()[0]);
    },
    onKeyDownHandler(e) {
      const { showDropdown, filteredCountriesBySearch, onSelectCountry } = this;
      const { selectedIndex } = this;

      if (showDropdown) {
        if (hasPressedArrowDownKey(e)) {
          e.preventDefault();
          this.selectedIndex = Math.min(
            selectedIndex + 1,
            filteredCountriesBySearch.length - 1
          );
          this.$refs.dropdown.scrollTop = this.focusedItemIndex() * 28;
        } else if (hasPressedArrowUpKey(e)) {
          e.preventDefault();
          this.selectedIndex = Math.max(selectedIndex - 1, 0);
          this.$refs.dropdown.scrollTop = this.focusedItemIndex() * 28 - 56;
        } else if (isEnter(e)) {
          e.preventDefault();
          onSelectCountry(filteredCountriesBySearch[selectedIndex]);
        }
      }
    },
    onSelectCountry(country) {
      this.activeCountryCode = country.id;
      this.searchCountry = '';
      this.activeDialCode = country.dial_code;
      this.$emit('setCode', country.dial_code);
      this.closeDropdown();
    },
    setActiveCountry() {
      const { phoneNumber } = this;
      if (!phoneNumber) return;
      const number = parsePhoneNumber(phoneNumber);
      if (number) {
        this.activeCountryCode = number.country;
        this.activeDialCode = number.countryCallingCode;
      }
    },
    toggleCountryDropdown() {
      if (this.readonly) return;
      this.showDropdown = !this.showDropdown;
      this.selectedIndex = -1;
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.$refs.searchbar.focus();
        });
      }
    },
    closeDropdown() {
      this.selectedIndex = -1;
      this.showDropdown = false;
    },
    onMaskedInput(e) {      
      this.applyMask(e.target.value);
    },
    onPaste(e) {
      e.preventDefault();
      const pasted = (e.clipboardData || window.clipboardData).getData('text');
      this.applyMask(pasted);
    },
    normalizeDigits(value) {
      return String(value || '')
        .replace(/\D/g, '')
        .slice(0, 11);
    },
    formatLocalDigits(rawDigits) {
      if (this.activeDialCode == '+55') {
        if (rawDigits.length <= 10) {
          return rawDigits.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
        }
        return rawDigits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
      }

      return rawDigits;
    },
    localDigitsFromValue(value) {
      const digits = this.normalizeDigits(value);
      const dialCodeDigits = this.normalizeDigits(this.activeDialCode);

      if (dialCodeDigits && digits.startsWith(dialCodeDigits)) {
        return digits.slice(dialCodeDigits.length);
      }

      return digits;
    },
    applyMask(value) {
      const raw = this.normalizeDigits(value);
      const masked = this.formatLocalDigits(raw);

      this.phoneNumber = masked;

      const e164 = `${this.activeDialCode}${raw}`;
      this.$emit('input', e164, this.activeDialCode);
    },
    maskPhoneNumber(phoneNumber){
      const normalizedPhoneNumber =
        phoneNumber === null || phoneNumber === undefined
          ? ''
          : String(phoneNumber).trim();

      if (normalizedPhoneNumber === '') {
        return '';
      }
      const numberParse = parsePhoneNumber(normalizedPhoneNumber);
      if (numberParse) {
        this.activeCountryCode = numberParse.country;
        this.activeDialCode = `+${numberParse.countryCallingCode}`;

        let masked = numberParse.countryCallingCode == 55 ? numberParse.formatNational() : numberParse.formatNational().replace(/\D/g, "");

        if (this.encrypt) {
          const sub = masked.substring((numberParse.countryCallingCode == 55 ? 4 : 2), masked.length - 4);
          masked = masked.replace(sub, '*'.repeat(sub.length)) || '';
        }

        return masked;
      }

      const localValue = this.localDigitsFromValue(normalizedPhoneNumber);
      return this.formatLocalDigits(localValue);
    }
  },
};
</script>
<style scoped lang="scss">
.phone-input--wrap {
  position: relative;

  .phone-input {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: var(--space-normal);
    border: 1px solid var(--s-200);
    border-radius: var(--border-radius-normal);

    &.has-error {
      border: 1px solid var(--r-400);
    }
  }

  .country-emoji--wrap {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-small);
    background: var(--s-25);
    height: 4rem;
    width: 5.2rem;
    border-radius: var(--border-radius-normal) 0 0 var(--border-radius-normal);
    padding: var(--space-small) var(--space-smaller) var(--space-small)
      var(--space-small);

    h5 {
      margin-bottom: 0;
    }
  }

  .country-dial--code {
    display: flex;
    color: var(--s-300);
    font-size: var(--space-normal);
    font-weight: normal;
    line-height: 1.5;
    padding: var(--space-small) 0 var(--space-small) var(--space-small);
    margin-right: 2px;
  }

  .phone-input--field {
    margin-bottom: 0;
    border: 0;
  }

  .country-dropdown {
    z-index: var(--z-index-low);
    position: absolute;
    height: var(--space-giga);
    width: 20rem;
    overflow-y: auto;
    top: 4rem;
    border-radius: var(--border-radius-default);
    padding: 0 0 var(--space-smaller) 0;
    background-color: var(--white);
    box-shadow: var(--shadow-context-menu);
    border-radius: var(--border-radius-normal);

    .dropdown-search--wrap {
      top: 0;
      position: sticky;
      background-color: var(--white);
      padding: var(--space-smaller);

      .dropdown-search {
        height: var(--space-large);
        margin-bottom: 0;
        font-size: var(--font-size-small);
        border: 1px solid var(--s-200) !important;
      }
    }

    .country-dropdown--item {
      display: flex;
      align-items: center;
      height: 2.8rem;
      padding: 0 var(--space-smaller);
      cursor: pointer;

      &.active {
        background-color: var(--s-50);
      }

      &.focus {
        background-color: var(--s-25);
      }

      &:hover {
        background-color: var(--s-50);
      }

      .country-emoji {
        font-size: var(--font-size-default);
        margin-right: var(--space-smaller);
      }

      .country-name {
        max-width: 12rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .country-dial-code {
        margin-left: var(--space-smaller);
        color: var(--s-300);
        font-size: var(--font-size-mini);
      }
    }

    .no-results {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--s-500);
      margin-top: var(--space-normal);
    }
  }

  &.phone-input--wrap--readonly {
    .country-emoji--wrap {
      cursor: not-allowed;
    }
  }
}
</style>
